import React, { useState } from 'react';
import { X, Loader2, CheckCircle, AlertTriangle } from 'lucide-react';
import { useCrmConnection } from '../../hooks/useCrmConnection';

interface Props {
  companyId: string | undefined;
  open: boolean;
  onClose: () => void;
  onConnected?: () => void;
}

export const ConnectMyCrmModal: React.FC<Props> = ({
  companyId,
  open,
  onClose,
  onConnected,
}) => {
  const { connect } = useCrmConnection(companyId);
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [authUrl, setAuthUrl] = useState('');
  const [scope, setScope] = useState('api');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connected, setConnected] = useState(false);

  if (!open) return null;

  const handleSubmit = async () => {
    if (!clientId.trim() || !clientSecret.trim() || !authUrl.trim()) {
      setError('Client ID, client secret, and auth URL are required');
      return;
    }
    if (!authUrl.startsWith('https://')) {
      setError('Auth URL must use HTTPS');
      return;
    }
    setError(null);
    setSaving(true);
    try {
      await connect({
        clientId: clientId.trim(),
        clientSecret: clientSecret.trim(),
        authUrl: authUrl.trim(),
        scope: scope.trim() || undefined,
      });
      setConnected(true);
      onConnected?.();
      setTimeout(() => {
        onClose();
        setConnected(false);
        setClientId('');
        setClientSecret('');
        setAuthUrl('');
        setScope('api');
      }, 1200);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
      <div className="bg-gray-900 border border-gray-700 rounded-lg shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-700">
          <h2 className="text-base font-semibold text-white">Connect MyCRM</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-1.5 rounded hover:bg-gray-800 text-gray-400"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        <div className="p-5 space-y-3">
          <div className="rounded-md border border-blue-500/30 bg-blue-500/5 p-3 text-xs text-blue-300 flex gap-2">
            <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <div>
              In MyCRM: Profile Management → API Access Management. Copy
              your Client ID, Client Secret, and the Auth URL (Okta token
              endpoint, specific to your tenant).
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">
              Client ID
            </label>
            <input
              type="text"
              value={clientId}
              onChange={(e) => setClientId(e.target.value)}
              placeholder="0oazrldwbzff2bVmr357"
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">
              Client Secret
            </label>
            <input
              type="password"
              value={clientSecret}
              onChange={(e) => setClientSecret(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">
              Auth URL (Okta token endpoint)
            </label>
            <input
              type="text"
              value={authUrl}
              onChange={(e) => setAuthUrl(e.target.value)}
              placeholder="https://auth.mycrm.finance/oauth2/{authServerId}/v1/token"
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-xs"
            />
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-400 mb-1">
              OAuth scope (default: api)
            </label>
            <input
              type="text"
              value={scope}
              onChange={(e) => setScope(e.target.value)}
              className="w-full px-3 py-2 rounded-md bg-gray-800 border border-gray-700 text-white font-mono text-xs"
            />
            <p className="text-[11px] text-gray-500 mt-1">
              LMG provisions narrower scopes (api.read, api.leads) per
              tenant. If unsure, leave as &ldquo;api&rdquo;.
            </p>
          </div>
          {error && (
            <div className="rounded-md border border-red-500/30 bg-red-500/5 p-3 text-xs text-red-300">
              {error}
            </div>
          )}
          {connected && (
            <div className="rounded-md border border-emerald-500/30 bg-emerald-500/5 p-3 text-xs text-emerald-300 flex items-center gap-2">
              <CheckCircle className="w-4 h-4" /> MyCRM validated and stored.
              First sync runs nightly or via Sync Now.
            </div>
          )}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-4 border-t border-gray-700">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 rounded-md text-sm text-gray-300 hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSubmit}
            disabled={saving || connected}
            className="px-4 py-2 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm disabled:opacity-50 inline-flex items-center gap-1.5"
          >
            {saving && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
            {saving ? 'Validating…' : 'Connect'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ConnectMyCrmModal;
